import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowLeft, Users, Target, UserCheck, Tag, Search, Crown, Shield, Trash2, Copy, Link, UserPlus, ShieldCheck, ShieldOff, BarChart3, Cloud, LogOut, MessageCircle, Clock, Flame } from "lucide-react";
import Layout from "../components/Layout";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import { useConfirm } from "../hooks/useConfirm";
import { waGroupsAPI } from "../services/api";
import api from "../services/api";

const WhatsappGroupDetail = () => {
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const showConfirm = useConfirm();

  const [group, setGroup] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [grupoCategorias, setGrupoCategorias] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParticipant, setSearchParticipant] = useState("");
  const [metaLocal, setMetaLocal] = useState(0);
  const [editingCategories, setEditingCategories] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  const [showWordCloud, setShowWordCloud] = useState(false);
  const [addPhone, setAddPhone] = useState("");
  const [insightsData, setInsightsData] = useState(null);
  const [wordCloudData, setWordCloudData] = useState(null);
  const [insightsDays, setInsightsDays] = useState(30);
  const [wordCloudDays, setWordCloudDays] = useState(30);
  const [activeWord, setActiveWord] = useState('');
  const [locationForm, setLocationForm] = useState({ cidade: '', bairro: '', estado: '', latitude: '', longitude: '', tags: [] });
  const [temperature, setTemperature] = useState(null);
  const [tempDays, setTempDays] = useState(7);

  const clienteId = user?.cliente_id;

  useEffect(() => {
    loadAll();
  }, [id]);

  useEffect(() => {
    if (clienteId && id) {
      waGroupsAPI.getTemperature(id, clienteId, { days: tempDays })
        .then(r => setTemperature(r.data)).catch(() => {});
    }
  }, [tempDays]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/grupos-whatsapp/${id}`, { params: { cliente_id: clienteId } });
      const groupData = res.data;
      setGroup(groupData);
      setMetaLocal(groupData.meta_participantes || 0);
      setLocationForm({
        cidade: groupData.cidade || '', bairro: groupData.bairro || '', estado: groupData.estado || '',
        latitude: groupData.latitude || '', longitude: groupData.longitude || '', tags: groupData.tags || [],
      });
      setParticipants(groupData.metadata?.participants || []);

      const catRes = await waGroupsAPI.getCategorias();
      setCategorias(catRes.data?.data || []);

      const valRes = await waGroupsAPI.getGrupoCategorias(id);
      setGrupoCategorias(valRes.data?.data || []);
    } catch (e) {
      toast.error("Erro ao carregar grupo");
      navigate("/grupos-wa");
    }

    // Insights e WordCloud (carrega em paralelo sem bloquear)
    api.get(`/grupos-whatsapp/${id}/insights`, { params: { cliente_id: clienteId, days: 30 } })
      .then(r => setInsightsData(r.data)).catch(() => {});
    api.get(`/grupos-whatsapp/${id}/wordcloud`, { params: { cliente_id: clienteId, days: 30 } })
      .then(r => setWordCloudData(r.data)).catch(() => {});
    waGroupsAPI.getTemperature(id, clienteId, { days: tempDays })
      .then(r => setTemperature(r.data)).catch(() => {});

    setLoading(false);
  };

  const handleSetResponsavel = async (contatoId) => {
    try {
      await waGroupsAPI.setResponsavel(id, contatoId || null);
      setGroup({ ...group, responsavel_contato_id: contatoId });
      toast.success("Responsável atualizado");
    } catch (e) { toast.error("Erro ao salvar"); }
  };

  const handleSetMeta = async () => {
    try {
      await waGroupsAPI.setMeta(id, metaLocal);
      setGroup({ ...group, meta_participantes: metaLocal });
      toast.success("Meta salva");
    } catch (e) { toast.error("Erro ao salvar"); }
  };

  const handleGeocode = async () => {
    const q = `${locationForm.bairro ? locationForm.bairro + ', ' : ''}${locationForm.cidade || ''}, ${locationForm.estado || 'Brasil'}`;
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=1`);
      const data = await res.json();
      if (data[0]) {
        setLocationForm(f => ({ ...f, latitude: parseFloat(data[0].lat), longitude: parseFloat(data[0].lon) }));
        toast.success('Coordenadas encontradas!');
      } else toast.warning('Localização não encontrada');
    } catch { toast.error('Erro ao geocodificar'); }
  };

  const handleSaveLocation = async () => {
    try {
      await api.patch(`/grupos-whatsapp/${id}/location`, locationForm);
      setGroup({ ...group, ...locationForm });
      toast.success('Localização salva!');
    } catch { toast.error('Erro ao salvar'); }
  };

  const handleSetCategoria = async (categoriaId, opcaoIds) => {
    try {
      // Manter valores de outras categorias + substituir a atual
      const outrasCategs = grupoCategorias.filter(v => v.categoria_id !== categoriaId);
      const novosValores = [
        ...outrasCategs,
        ...opcaoIds.filter(Boolean).map(opcaoId => ({ categoria_id: categoriaId, opcao_id: opcaoId }))
      ];
      await waGroupsAPI.setGrupoCategorias(id, novosValores.map(v => ({ categoria_id: v.categoria_id, opcao_id: v.opcao_id })));
      setGrupoCategorias(novosValores);
    } catch (e) { toast.error("Erro ao salvar categoria"); }
  };

  const handleRemoveParticipant = (participant) => {
    showConfirm({
      title: 'Remover Participante',
      message: `Remover ${participant.name || participant.phone} do grupo?`,
      confirmText: 'Remover',
      type: 'danger',
      onConfirm: async () => {
        try {
          await api.delete(`/grupos-whatsapp/${id}/participants/${participant.phone}?cliente_id=${clienteId}`);
          toast.success('Participante removido');
          setParticipants(prev => prev.filter(p => p.phone !== participant.phone));
        } catch { toast.error('Erro ao remover'); }
      }
    });
  };

  const handleLeaveGroup = () => {
    showConfirm({
      title: 'Sair do Grupo',
      message: `Deseja sair do grupo "${group?.nome}"?`,
      confirmText: 'Sair',
      type: 'warning',
      onConfirm: async () => {
        try {
          await api.post(`/grupos-whatsapp/${id}/leave`, { clienteId });
          toast.success('Saiu do grupo');
          navigate('/grupos-wa');
        } catch { toast.error('Erro ao sair do grupo'); }
      }
    });
  };

  const handleAddParticipant = async () => {
    const phone = addPhone.replace(/\D/g, '');
    if (!phone) return;
    try {
      await api.post(`/grupos-whatsapp/${id}/participants`, { phone, cliente_id: clienteId });
      toast.success('Participante adicionado');
      setAddPhone("");
      loadAll();
    } catch (e) { toast.error('Erro ao adicionar participante'); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado!");
  };

  const filteredParticipants = participants.filter(p => {
    if (!searchParticipant) return true;
    const term = searchParticipant.toLowerCase();
    return (p.name || "").toLowerCase().includes(term) || (p.phone || "").includes(term);
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </Layout>
    );
  }

  if (!group) return null;

  const metaPercent = group.meta_participantes > 0 
    ? Math.round((group.total_participantes / group.meta_participantes) * 100) 
    : 0;
  const admins = participants.filter(p => p.isAdmin || p.isSuperAdmin);
  const inviteLink = group.metadata?.invitationLink;

  const cardBg = darkMode ? "bg-zinc-800 border-gray-700" : "bg-white border-gray-200";
  const textMain = darkMode ? "text-white" : "text-gray-900";
  const textMuted = darkMode ? "text-gray-400" : "text-gray-500";
  const inputCls = darkMode ? "bg-zinc-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900";

  return (
    <Layout>
      {/* Header */}
      <div className="mb-6">
        <button onClick={() => navigate(-1)} className={`flex items-center gap-1 text-sm mb-3 ${textMuted} hover:opacity-80`}>
          <ArrowLeft size={16} /> Voltar para grupos
        </button>
        <div className="flex items-start gap-4">
          {group.foto ? (
            <img src={group.foto} alt={group.nome} className="h-20 w-20 rounded-xl object-cover" />
          ) : (
            <div className="h-20 w-20 rounded-xl bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white text-3xl font-bold flex-shrink-0">
              {group.nome?.charAt(0)?.toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className={`text-xl font-bold ${textMain}`}>{group.nome}</h1>
            {group.descricao && <p className={`text-sm mt-1 ${textMuted} line-clamp-3`}>{group.descricao}</p>}
            {inviteLink && (
              <button onClick={() => copyToClipboard(inviteLink)} className={`flex items-center gap-1 mt-2 text-xs text-blue-500 hover:text-blue-400`}>
                <Link size={12} /> Copiar link de convite
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className={`rounded-xl border p-4 text-center ${cardBg}`}>
          <p className={`text-2xl font-bold ${textMain}`}>{group.total_participantes}</p>
          <p className={`text-xs ${textMuted}`}>Participantes</p>
        </div>
        <div className={`rounded-xl border p-4 text-center ${cardBg}`}>
          <p className={`text-2xl font-bold ${group.is_admin ? "text-green-500" : "text-orange-500"}`}>
            {group.is_admin ? "Sim" : "Não"}
          </p>
          <p className={`text-xs ${textMuted}`}>Somos Admin</p>
        </div>
        <div className={`rounded-xl border p-4 text-center ${cardBg}`}>
          <p className={`text-2xl font-bold text-blue-500`}>{admins.length}</p>
          <p className={`text-xs ${textMuted}`}>Admins</p>
        </div>
        <div className={`rounded-xl border p-4 text-center ${cardBg}`}>
          <p className={`text-2xl font-bold ${metaPercent >= 100 ? "text-green-500" : textMain}`}>
            {metaPercent > 0 ? `${metaPercent}%` : "—"}
          </p>
          <p className={`text-xs ${textMuted}`}>Meta</p>
        </div>
      </div>

      {/* Temperatura / Engajamento */}
      {temperature && (
        <div className={`rounded-xl border p-4 mb-6 ${cardBg}`}>
          <div className="flex items-center justify-between mb-3">
            <h3 className={`text-sm font-semibold flex items-center gap-2 ${textMain}`}>
              <Flame size={16} /> Temperatura do Grupo
            </h3>
            <div className="flex gap-1">
              {[7, 14, 30].map(d => (
                <button key={d} onClick={() => setTempDays(d)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${tempDays === d ? 'bg-nomoz-orange text-white' : darkMode ? 'bg-zinc-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                  {d}d
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className={`rounded-lg p-3 text-center ${darkMode ? 'bg-zinc-900/50' : 'bg-gray-50'}`}>
              <p className="text-2xl">{temperature.temperature === 'hot' ? '🔥' : temperature.temperature === 'warm' ? '🌡️' : temperature.temperature === 'cold' ? '❄️' : '🧊'}</p>
              <p className={`text-xs font-medium mt-1 ${textMain}`}>
                {temperature.temperature === 'hot' ? 'Quente' : temperature.temperature === 'warm' ? 'Morno' : temperature.temperature === 'cold' ? 'Frio' : 'Congelado'}
              </p>
              <p className={`text-[10px] ${textMuted}`}>Score: {temperature.score}</p>
            </div>
            <div className={`rounded-lg p-3 text-center ${darkMode ? 'bg-zinc-900/50' : 'bg-gray-50'}`}>
              <p className={`text-xl font-bold ${textMain}`}>{temperature.mensagens}</p>
              <p className={`text-[10px] ${textMuted}`}>Mensagens</p>
            </div>
            <div className={`rounded-lg p-3 text-center ${darkMode ? 'bg-zinc-900/50' : 'bg-gray-50'}`}>
              <p className={`text-xl font-bold ${textMain}`}>{temperature.msgs_per_day}</p>
              <p className={`text-[10px] ${textMuted}`}>Msgs/dia</p>
            </div>
            <div className={`rounded-lg p-3 text-center ${darkMode ? 'bg-zinc-900/50' : 'bg-gray-50'}`}>
              <p className={`text-xl font-bold ${textMain}`}>{temperature.participantes_ativos}</p>
              <p className={`text-[10px] ${textMuted}`}>Ativos</p>
            </div>
            <div className={`rounded-lg p-3 text-center ${darkMode ? 'bg-zinc-900/50' : 'bg-gray-50'}`}>
              <p className={`text-xl font-bold ${textMain}`}>{temperature.engagement_rate}%</p>
              <p className={`text-[10px] ${textMuted}`}>Engajamento</p>
            </div>
          </div>
        </div>
      )}

      {/* Grid principal */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        {/* Coluna esquerda - Config */}
        <div className="space-y-4">
          {/* Responsável */}
          <div className={`rounded-xl border p-4 ${cardBg}`}>
            <h3 className={`text-sm font-semibold flex items-center gap-2 mb-3 ${textMain}`}>
              <UserCheck size={16} /> Responsável
            </h3>
            <select
              value={group.responsavel_contato_id || ""}
              onChange={(e) => handleSetResponsavel(e.target.value)}
              className={`w-full px-3 py-2 rounded-lg border text-sm ${inputCls}`}
            >
              <option value="">Selecionar responsável...</option>
              {participants.map((p, i) => (
                <option key={i} value={p.phone}>
                  {p.name || p.phone} {p.isAdmin ? '(Admin)' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Meta */}
          <div className={`rounded-xl border p-4 ${cardBg}`}>
            <h3 className={`text-sm font-semibold flex items-center gap-2 mb-3 ${textMain}`}>
              <Target size={16} /> Meta de Participantes
            </h3>
            <div className="flex items-center gap-2 mb-2">
              <input
                type="number"
                value={metaLocal}
                onChange={(e) => setMetaLocal(Number(e.target.value))}
                onBlur={handleSetMeta}
                className={`w-24 px-3 py-2 rounded-lg border text-sm ${inputCls}`}
              />
              <span className={`text-sm ${textMuted}`}>
                {group.total_participantes} atual
              </span>
            </div>
            {group.meta_participantes > 0 && (
              <>
                <div className={`w-full h-2.5 rounded-full ${darkMode ? "bg-gray-700" : "bg-gray-200"}`}>
                  <div
                    className={`h-2.5 rounded-full transition-all ${metaPercent >= 100 ? "bg-green-500" : "bg-blue-500"}`}
                    style={{ width: `${Math.min(100, metaPercent)}%` }}
                  />
                </div>
                <p className={`text-xs mt-1 ${textMuted}`}>{metaPercent}% alcançado</p>
              </>
            )}
          </div>

          {/* Categorias */}
          <div className={`rounded-xl border p-4 ${cardBg}`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className={`text-sm font-semibold flex items-center gap-2 ${textMain}`}>
                <Tag size={16} /> Categorias
              </h3>
              <button
                onClick={() => setEditingCategories(!editingCategories)}
                className={`text-xs px-2.5 py-1 rounded-lg transition-colors ${
                  editingCategories 
                    ? 'bg-blue-600 text-white' 
                    : darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {editingCategories ? 'Concluir' : 'Editar'}
              </button>
            </div>
            {categorias.length === 0 ? (
              <p className={`text-xs ${textMuted}`}>Nenhuma categoria criada. Configure em ⚙️ na listagem.</p>
            ) : editingCategories ? (
              /* Modo edição - mostra tudo */
              <div className="space-y-3">
                {categorias.map(cat => {
                  const valoresAtuais = grupoCategorias.filter(v => v.categoria_id === cat.id);
                  const opcaoIds = valoresAtuais.map(v => v.opcao_id);
                  return (
                    <div key={cat.id}>
                      <label className={`text-xs font-medium block mb-1.5 ${textMuted}`}>
                        {cat.label} {cat.multiplo && <span className="text-blue-500">(múltiplo)</span>}
                      </label>
                      {cat.multiplo ? (
                        <div className="flex flex-wrap gap-1.5">
                          {(cat.opcoes || []).map(opc => {
                            const selected = opcaoIds.includes(opc.id);
                            return (
                              <button
                                key={opc.id}
                                onClick={() => {
                                  const novos = selected
                                    ? valoresAtuais.filter(v => v.opcao_id !== opc.id)
                                    : [...valoresAtuais, { categoria_id: cat.id, opcao_id: opc.id }];
                                  handleSetCategoria(cat.id, novos.map(v => v.opcao_id));
                                }}
                                className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
                                  selected
                                    ? 'bg-blue-600 text-white shadow-sm'
                                    : darkMode ? 'bg-zinc-700 text-gray-400 hover:bg-zinc-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                }`}
                              >
                                {opc.nome}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <select
                          value={opcaoIds[0] || ""}
                          onChange={(e) => handleSetCategoria(cat.id, e.target.value ? [e.target.value] : [])}
                          className={`w-full px-2.5 py-1.5 rounded-lg border text-sm ${inputCls}`}
                        >
                          <option value="">—</option>
                          {(cat.opcoes || []).map(opc => (
                            <option key={opc.id} value={opc.id}>{opc.nome}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Modo visualização - só mostra atribuídos */
              <div className="space-y-2">
                {categorias.map(cat => {
                  const valoresAtuais = grupoCategorias.filter(v => v.categoria_id === cat.id);
                  if (valoresAtuais.length === 0) return null;
                  return (
                    <div key={cat.id}>
                      <span className={`text-[10px] uppercase tracking-wide ${textMuted}`}>{cat.label}</span>
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {valoresAtuais.map(v => {
                          const opc = (cat.opcoes || []).find(o => o.id === v.opcao_id);
                          return opc ? (
                            <span key={v.opcao_id} className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                              darkMode ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-50 text-blue-700'
                            }`}>
                              {opc.nome}
                            </span>
                          ) : null;
                        })}
                      </div>
                    </div>
                  );
                }).filter(Boolean)}
                {grupoCategorias.length === 0 && (
                  <p className={`text-xs italic ${textMuted}`}>Nenhuma categoria atribuída</p>
                )}
              </div>
            )}
          </div>

          {/* Localização */}
          <div className={`rounded-xl border p-4 ${cardBg}`}>
            <h3 className={`text-sm font-semibold flex items-center gap-2 mb-3 ${textMain}`}>
              📍 Localização
            </h3>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={`text-[10px] font-medium ${textMuted}`}>Cidade</label>
                  <input
                    className={`w-full px-2.5 py-1.5 rounded-lg border text-sm ${inputCls}`}
                    value={locationForm.cidade}
                    onChange={e => setLocationForm(f => ({ ...f, cidade: e.target.value }))}
                    placeholder="Campinas"
                  />
                </div>
                <div>
                  <label className={`text-[10px] font-medium ${textMuted}`}>Estado</label>
                  <input
                    className={`w-full px-2.5 py-1.5 rounded-lg border text-sm ${inputCls}`}
                    value={locationForm.estado}
                    onChange={e => setLocationForm(f => ({ ...f, estado: e.target.value }))}
                    placeholder="SP"
                  />
                </div>
              </div>
              <div>
                <label className={`text-[10px] font-medium ${textMuted}`}>Bairro</label>
                <input
                  className={`w-full px-2.5 py-1.5 rounded-lg border text-sm ${inputCls}`}
                  value={locationForm.bairro}
                  onChange={e => setLocationForm(f => ({ ...f, bairro: e.target.value }))}
                  placeholder="Cambuí"
                />
              </div>
              <button
                onClick={handleGeocode}
                className={`w-full px-3 py-1.5 rounded-lg text-xs font-medium ${darkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
              >
                🔍 Buscar coordenadas
              </button>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={`text-[10px] font-medium ${textMuted}`}>Latitude</label>
                  <input
                    className={`w-full px-2.5 py-1.5 rounded-lg border text-sm ${inputCls}`}
                    type="number" step="any"
                    value={locationForm.latitude}
                    onChange={e => setLocationForm(f => ({ ...f, latitude: e.target.value }))}
                  />
                </div>
                <div>
                  <label className={`text-[10px] font-medium ${textMuted}`}>Longitude</label>
                  <input
                    className={`w-full px-2.5 py-1.5 rounded-lg border text-sm ${inputCls}`}
                    type="number" step="any"
                    value={locationForm.longitude}
                    onChange={e => setLocationForm(f => ({ ...f, longitude: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <label className={`text-[10px] font-medium ${textMuted}`}>Tags (separadas por vírgula)</label>
                <input
                  className={`w-full px-2.5 py-1.5 rounded-lg border text-sm ${inputCls}`}
                  value={(locationForm.tags || []).join(', ')}
                  onChange={e => setLocationForm(f => ({ ...f, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) }))}
                  placeholder="leads, vip, campinas"
                />
              </div>
              <button
                onClick={handleSaveLocation}
                className="w-full px-3 py-2 bg-nomoz-orange text-white rounded-lg text-sm font-medium hover:bg-nomoz-orange-dark"
              >
                💾 Salvar localização
              </button>
              {/* Minimapa */}
              {locationForm.latitude && locationForm.longitude && (
                <div className="rounded-lg overflow-hidden border" style={{ height: 180 }}>
                  <iframe
                    title="Mapa"
                    width="100%"
                    height="180"
                    style={{ border: 0 }}
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${parseFloat(locationForm.longitude) - 0.01},${parseFloat(locationForm.latitude) - 0.01},${parseFloat(locationForm.longitude) + 0.01},${parseFloat(locationForm.latitude) + 0.01}&layer=mapnik&marker=${locationForm.latitude},${locationForm.longitude}`}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Info técnica */}
          <div className={`rounded-xl border p-4 ${cardBg}`}>
            <h3 className={`text-sm font-semibold mb-3 ${textMain}`}>Informações</h3>
            <div className="space-y-2">
              <div>
                <p className={`text-xs ${textMuted}`}>ID do Grupo</p>
                <div className="flex items-center gap-1">
                  <p className={`font-mono text-xs ${textMain} truncate`}>{group.group_id}</p>
                  <button onClick={() => copyToClipboard(group.group_id)} className="text-blue-500 hover:text-blue-400">
                    <Copy size={12} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Coluna central e direita - Participantes */}
        <div className={`lg:col-span-2 rounded-xl border overflow-hidden flex flex-col max-h-[1140px] ${cardBg}`}>
          <div className={`p-4 border-b shrink-0 ${darkMode ? "border-gray-700" : "border-gray-200"}`}>
            <div className="flex items-center justify-between">
              <h3 className={`text-sm font-semibold flex items-center gap-2 ${textMain}`}>
                <Users size={16} /> Participantes ({participants.length})
              </h3>
              <div className="flex items-center gap-2">
                {/* Adicionar participante */}
                {group.is_admin && (
                  <div className="flex items-center gap-1">
                    <input
                      value={addPhone}
                      onChange={(e) => setAddPhone(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleAddParticipant()}
                      placeholder="Telefone..."
                      className={`px-2 py-1.5 rounded border text-xs w-28 ${inputCls}`}
                    />
                    <button onClick={handleAddParticipant} className="p-1.5 rounded bg-green-600 text-white hover:bg-green-700" title="Adicionar participante">
                      <UserPlus size={12} />
                    </button>
                  </div>
                )}
                <div className="relative">
                  <Search size={14} className={`absolute left-2.5 top-2.5 ${textMuted}`} />
                  <input
                    value={searchParticipant}
                    onChange={(e) => setSearchParticipant(e.target.value)}
                    placeholder="Buscar..."
                    className={`pl-8 pr-3 py-2 rounded-lg border text-xs w-40 ${inputCls}`}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            {filteredParticipants.map((p, idx) => (
              <div key={idx} className={`flex items-center justify-between px-4 py-2.5 border-b last:border-b-0 ${darkMode ? "border-gray-700/50" : "border-gray-100"}`}>
                <div className="flex items-center gap-3">
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-semibold ${
                    p.isSuperAdmin ? "bg-red-500" : p.isAdmin ? "bg-green-500" : darkMode ? "bg-zinc-600" : "bg-gray-300 text-gray-700"
                  }`}>
                    {p.name?.charAt(0)?.toUpperCase() || p.phone?.slice(-2)}
                  </div>
                  <div>
                    <p className={`text-sm ${textMain}`}>{p.name || p.phone}</p>
                    {p.name && <p className={`text-xs ${textMuted}`}>{p.phone}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {p.isSuperAdmin && (
                    <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700">
                      <Crown size={10} /> Dono
                    </span>
                  )}
                  {p.isAdmin && !p.isSuperAdmin && (
                    <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-100 text-green-700">
                      <Shield size={10} /> Admin
                    </span>
                  )}
                  {group.is_admin && !p.isSuperAdmin && (
                    <button
                      onClick={() => handleRemoveParticipant(p)}
                      className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-400 hover:text-red-600"
                      title="Remover do grupo"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {filteredParticipants.length === 0 && (
              <p className={`text-center py-8 text-sm ${textMuted}`}>Nenhum participante encontrado</p>
            )}
          </div>
        </div>
      </div>

      {/* Insights inline */}
      {insightsData && (insightsData.summary?.total_messages > 0 || insightsData.daily_trend?.length > 0) ? (
        <div className={`rounded-xl border p-4 mt-6 ${cardBg}`}>
          <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${textMain}`}>
            <BarChart3 size={16} /> Insights ({insightsData.summary?.total_messages || 0} msgs em {insightsDays}d)
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {[
              { label: 'Msgs/dia', value: insightsData.summary?.msgs_per_day || 0 },
              { label: 'Ativos', value: insightsData.summary?.active_participants || 0 },
              { label: 'Lurkers', value: `${insightsData.summary?.lurker_pct || 0}%` },
              { label: 'Pico', value: insightsData.summary?.peak_hour ? `${insightsData.summary.peak_hour.hour}h` : '—' },
            ].map((c, i) => (
              <div key={i} className={`p-2.5 rounded-lg ${darkMode ? 'bg-zinc-700/50' : 'bg-gray-50'}`}>
                <p className={`text-xs ${textMuted}`}>{c.label}</p>
                <p className={`text-lg font-bold ${textMain}`}>{c.value}</p>
              </div>
            ))}
          </div>

          {/* Top Participants */}
          {insightsData.top_participants?.length > 0 && (
            <div>
              <h4 className={`text-xs font-semibold mb-2 ${textMuted}`}>🏆 Top Participantes</h4>
              <div className="space-y-1">
                {insightsData.top_participants.slice(0, 10).map((p, i) => {
                  const maxMsgs = insightsData.top_participants[0].messages;
                  const pct = (p.messages / maxMsgs) * 100;
                  return (
                    <div key={p.phone} className="flex items-center gap-2">
                      <span className={`text-xs w-4 text-right font-bold ${i < 3 ? 'text-orange-500' : textMuted}`}>{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-medium truncate ${textMain}`}>{p.name || p.phone}</span>
                          <span className={`text-[10px] ${textMuted}`}>{p.messages} msgs</span>
                        </div>
                        <div className={`h-1 rounded-full mt-0.5 ${darkMode ? 'bg-zinc-700' : 'bg-gray-200'}`}>
                          <div className="h-full rounded-full bg-orange-500" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Daily Trend */}
          {insightsData.daily_trend?.length > 1 && (() => {
            const maxMsgs = Math.max(...insightsData.daily_trend.map(d => d.messages), 1);
            return (
              <div className="mt-4">
                <h4 className={`text-xs font-semibold mb-2 ${textMuted}`}>📈 Tendência Diária</h4>
                <div className="flex items-end gap-0.5" style={{ height: 60 }}>
                  {insightsData.daily_trend.map((d, i) => {
                    const h = (d.messages / maxMsgs) * 100;
                    return (
                      <div key={i} className="flex-1" title={`${new Date(d.date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}: ${d.messages} msgs`}>
                        <div className="w-full rounded-t bg-orange-500/70" style={{ height: `${h}%`, minHeight: d.messages > 0 ? 2 : 0 }} />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      ) : (
        <div className={`rounded-xl border p-4 mt-6 ${cardBg}`}>
          <h3 className={`text-sm font-semibold flex items-center gap-2 mb-2 ${textMain}`}>
            <BarChart3 size={16} /> Insights & Nuvem de Palavras
          </h3>
          <p className={`text-sm ${textMuted}`}>Sem atividade registrada nos últimos 30 dias neste grupo.</p>
        </div>
      )}

      {/* Word Cloud inline */}
      {wordCloudData && wordCloudData.words?.length > 0 && (
        <div className={`rounded-xl border p-4 mt-4 ${cardBg}`}>
          <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${textMain}`}>
            <Cloud size={16} /> Nuvem de Palavras
          </h3>
          <div className="flex flex-wrap gap-1.5 justify-center p-3">
            {wordCloudData.words.slice(0, 50).map((w, i) => {
              const maxCount = wordCloudData.words[0].count;
              const ratio = w.count / maxCount;
              const size = 12 + ratio * 24;
              const COLORS = ['#f97316','#ef4444','#8b5cf6','#3b82f6','#10b981','#f59e0b','#ec4899','#06b6d4','#84cc16','#6366f1'];
              return (
                <span key={w.word} className="font-semibold cursor-default" title={`${w.word}: ${w.count}x`}
                  style={{ fontSize: size, color: COLORS[i % COLORS.length], opacity: 0.5 + ratio * 0.5 }}>
                  {w.word}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Sair do grupo */}
      <div className="mt-6 mb-8">
        <button
          onClick={handleLeaveGroup}
          className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${darkMode ? "bg-red-900/50 text-red-300 hover:bg-red-900" : "bg-red-50 text-red-600 hover:bg-red-100"}`}
        >
          <LogOut size={16} /> Sair do Grupo
        </button>
      </div>
    </Layout>
  );
};

export default WhatsappGroupDetail;
